<template>
  <div>
    <v-row v-if="site" justify="start" align="center" class="text-center text-sm-left">
      <v-col v-if="site.image" cols="12" sm="auto">
        <v-avatar size="100">
          <v-img :src="site.image ? site.image.contentUrl : require('@/assets/no-img.svg')"></v-img>
        </v-avatar>
      </v-col>
      <v-col cols="12" sm="auto">
        <div class="headline mb-2">{{ site.name }}</div>
        <template>
          <v-menu v-if="currentAgreements.length > 1" offset-y>
            <template v-slot:activator="{ on }">
              <v-chip v-on="on">
                <v-icon left :color="agreement.contractuallyActivated && !agreement.volumeExceeded ? 'green' : 'red'">{{
                  getTechnologyIcon(agreement.servicePlan.technology)
                }}</v-icon>
                {{ agreement.alias ? agreement.alias : agreement.servicePlan.name }}

                <v-icon right>mdi-chevron-down</v-icon>
              </v-chip>
            </template>
            <v-list>
              <v-list-item
                v-for="currentAgreement in currentAgreements"
                :key="currentAgreement['@id']"
                :to="{
                  name: 'AgreementShow',
                  params: { id: $iriToId(currentAgreement['@id']) }
                }"
              >
                <v-list-item-icon
                  ><v-icon left :color="currentAgreement.contractuallyActivated ? 'green' : 'red'">{{
                    getTechnologyIcon(currentAgreement.servicePlan.technology)
                  }}</v-icon></v-list-item-icon
                >
                <v-list-item-title>
                  {{ currentAgreement.alias ? currentAgreement.alias : currentAgreement.servicePlan.name }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-chip v-else>
            <v-icon left :color="agreement.contractuallyActivated ? 'green' : 'red'">{{
              getTechnologyIcon(agreement.servicePlan.technology)
            }}</v-icon>
            {{ agreement.alias ? agreement.alias : agreement.servicePlan.name }}
          </v-chip>
        </template>
      </v-col>
    </v-row>

    <Alert
      v-if="filteredPeriods"
      :agreement="agreement"
      :current-period="currentPeriod"
      :next-period="nextPeriod"
      :filtered-periods="filteredPeriods"
      :technology-icon="getTechnologyIcon(agreement.servicePlan.technology)"
      :has-allowed-tokens="hasAllowedTokens"
      :show-date="showDate.getFullYear()"
      :min-start-date="minStartDate"
      @showPeriodCreateDialog="showPeriodCreateDialog = $event"
      @showStopReactivateDialog="showStopReactivateDialog = $event"
      @showVolumeTokenCreateDialog="showVolumeTokenCreateDialog = $event"
      @openAutomaticRenewalDialog="renewalDialog = $event"
      @stopAutomaticRenewal="stopRenewalDialog = $event"
    />

    <v-row>
      <v-col cols="12" md="8" class="py-0">
        <v-row>
          <CurrentMonthTokens v-if="currentPeriod" :service-period="currentPeriod" />

          <v-col cols="12">
            <v-card
              v-if="
                'recurring' === agreement.servicePlan.type &&
                  !agreement.maxDeactivation &&
                  !agreement.servicePlan.maxDeactivation
              "
              outlined
            >
              <v-toolbar flat>
                <v-toolbar-title>{{ $t('message.manage_your_service') }}</v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <v-row no-gutters justify="center" class="text-center">
                  <div class="py-12">
                    <v-icon size="48" class="mb-1">mdi-information-outline</v-icon>
                    <div class="title font-weight-regular">
                      {{ $t('message.your_service_is_always_active') }}
                    </div>
                    <div class="grey--text mb-3">
                      {{ $t('message.your_service_is_always_active_info') }}
                    </div>
                    <v-btn color="primary" depressed :to="{ name: 'support' }">
                      {{ $t('message.view_contact_details') }}
                    </v-btn>
                  </div>
                </v-row>
              </v-card-text>
            </v-card>
            <v-card v-else outlined :loading="isLoadingPeriods">
              <v-toolbar flat>
                <v-toolbar-title>
                  {{ $t('message.calendar') }}
                </v-toolbar-title>
              </v-toolbar>

              <v-card-text class="pt-0">
                <template
                  v-if="'on_demand' === agreement.servicePlan.type || 'fixed_term' === agreement.servicePlan.type"
                >
                  {{
                    $t('message.incoming_activations_info', {
                      servicePlanName: agreement.servicePlan.name
                    })
                  }}
                </template>
                <template v-else>
                  {{
                    $t('message.incoming_pauses_info', {
                      servicePlanName: agreement.servicePlan.name
                    })
                  }}
                </template>
              </v-card-text>
              <v-divider></v-divider>
              <v-row no-gutters :class="{ 'flex-nowrap': $vuetify.breakpoint.mdAndUp }">
                <v-col
                  cols="12"
                  md="6"
                  :style="{
                    height: $vuetify.breakpoint.xsOnly ? '370px' : '420px'
                  }"
                >
                  <template v-if="servicePeriods">
                    <YearCalendar
                      v-if="'fixed_term' === agreement.servicePlan.type || 'day' !== agreement.servicePlan.granularity"
                      :show-date="showDate"
                      :events="periodsToEvents(servicePeriods)"
                      :disabled="!!agreement.automaticRenewal"
                      :min-start-date="minStartDate"
                      @click-event="clickCalendarEvent"
                      @click-date="clickCalendarDay"
                    />
                    <calendar-view
                      v-else
                      :display-period-uom="agreement.servicePlan.allowedFixedTerms.length > 0 ? 'year' : 'month'"
                      :show-date="showDate"
                      :starting-day-of-week="'es' === $i18n.locale ? 1 : 0"
                      :events="periodsToEvents(servicePeriods)"
                      class="app-calendar border-bottom"
                      :event-top="eventTop"
                      :date-classes="dateClasses"
                      @click-event="clickCalendarEvent"
                      @click-date="clickCalendarDay"
                    >
                      <app-calendar-view-header
                        slot="header"
                        slot-scope="t"
                        :header-props="t.headerProps"
                        @input="setShowDate"
                      />
                    </calendar-view>
                  </template>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  class="d-flex justify-center"
                  :class="{ 'border-left': $vuetify.breakpoint.smAndUp }"
                >
                  <template v-if="filteredPeriods && automaticRenewalPeriod">
                    <v-list
                      class="flex-fill pa-0"
                      dense
                      :two-line="
                        'on_demand' === agreement.servicePlan.type || 'fixed_term' === agreement.servicePlan.type
                      "
                    >
                      <template v-for="(period, index) in [...filteredPeriods.current, ...filteredPeriods.incoming]">
                        <v-divider v-if="index > 0" :key="index" class="ml-12"></v-divider>
                        <v-list-item
                          :key="period.id"
                          :class="{
                            'list-tile-pending': 'recurring' !== agreement.servicePlan.type && !period.paid,
                            'green lighten-5':
                              0 === index && automaticRenewalPeriod && 'recurring' !== agreement.servicePlan.type,
                            'blue lighten-5':
                              0 === index && period.type === 'swap' && 'recurring' === agreement.servicePlan.type,
                            'red lighten-5':
                              0 === index && automaticRenewalPeriod && 'recurring' === agreement.servicePlan.type
                          }"
                          @click="openPeriodDialog(period)"
                        >
                          <v-list-item-icon class="mb-0 mr-2">
                            <v-icon
                              :color="
                                'recurring' === agreement.servicePlan.type && period.type === 'swap'
                                  ? 'blue'
                                  : 'recurring' === agreement.servicePlan.type
                                  ? 'red'
                                  : 'green'
                              "
                              size="16"
                              >{{
                                currentPeriod && period['@id'] === currentPeriod['@id'] ? 'mdi-timelapse' : 'mdi-circle'
                              }}
                            </v-icon>
                          </v-list-item-icon>
                          <v-list-item-content v-if="period.airtimePlan">
                            <v-list-item-title>
                              <span>
                                {{ period.airtimePlan.name }}
                              </span>
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              <span v-if="period.airtimePlan.downloadMir && period.airtimePlan.uploadMir">
                                {{ period.airtimePlan.downloadMir }} kbps / {{ period.airtimePlan.uploadMir }} kbps
                              </span>
                              <span v-else-if="period.airtimePlan.monthlyVolume">
                                {{ period.airtimePlan.monthlyVolume }} GB/{{ $t('message.month').toLowerCase() }}
                              </span>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-content v-else>
                            <v-list-item-title>
                              <span>
                                {{ $moment(period.startsAt).format('L') }}
                                <template
                                  v-if="
                                    period.endsAt &&
                                      $moment(period.endsAt).subtract(1, 'days') > $moment(period.startsAt)
                                  "
                                >
                                  <v-icon size="16">mdi-ray-start-arrow</v-icon>
                                  {{
                                    $moment(period.endsAt)
                                      .subtract(1, 'days')
                                      .format('L')
                                  }}
                                </template>
                                <template v-else-if="!period.endsAt">
                                  <v-icon size="16">mdi-ray-start-arrow</v-icon>
                                  {{ $t('message.indefinite_stop') }}
                                </template>
                              </span>
                            </v-list-item-title>
                          </v-list-item-content>
                          <template v-if="period.airtimePlan">
                            <v-list-item-content class="text-right">
                              <v-list-item-subtitle>
                                <span>
                                  {{ $moment(period.startsAt).format('L') }}
                                  <template v-if="period.endsAt">
                                    ({{ $moment(period.endsAt) | moment('from', $moment(period.startsAt), true) }})
                                  </template>
                                </span>
                                <br />
                                <v-progress-circular
                                  v-if="isLoadingPayPeriod && isLoadingPayPeriod['@id'] === period['@id']"
                                  size="25"
                                  indeterminate
                                  color="blue"
                                />
                                <a
                                  v-else-if="false === period.paid && 'stop' !== period.type && 'swap' !== period.type"
                                  class="orange--text text-uppercase"
                                  @click.stop="payPeriod(period)"
                                  >{{ $t('message.pay_now') }}</a
                                >
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-list-item>
                      </template>
                    </v-list>
                  </template>
                  <template v-else-if="filteredPeriods">
                    <div class="text-center my-6 align-self-center">
                      <template v-if="'recurring' === agreement.servicePlan.type">
                        <v-icon color="grey" size="48" class="mb-1">mdi-calendar-blank</v-icon>
                        <div class="title grey--text font-weight-regular mb-3">
                          {{ $t('message.no_scheduled_stops') }}
                        </div>
                        <v-btn depressed color="primary" @click="showPeriodCreateDialog = true">{{
                          $t('message.request_stop')
                        }}</v-btn>
                      </template>
                      <template
                        v-if="'on_demand' === agreement.servicePlan.type || 'fixed_term' === agreement.servicePlan.type"
                      >
                        <v-icon color="grey" size="48" class="mb-1">mdi-calendar-blank</v-icon>
                        <div class="title grey--text font-weight-regular mb-3">
                          {{ $t('message.no_scheduled_activations') }}
                        </div>
                        <v-btn
                          :disabled="!!agreement.automaticRenewal"
                          depressed
                          color="primary"
                          @click="showPeriodCreateDialog = true"
                          >{{ $t('message.request_activation') }}</v-btn
                        >
                      </template>
                    </div>
                  </template>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col v-if="agreement.airtimeBalances.length > 0" cols="12">
            <v-card outlined>
              <v-toolbar flat>
                <v-toolbar-title>{{ $t('message.balance') }}</v-toolbar-title>
              </v-toolbar>

              <v-data-list>
                <v-data-list-item v-for="airtimeBalance in positiveAirtimeBalance" :key="airtimeBalance['@id']">
                  <v-data-list-label>{{ airtimeBalance.airtimePlan.name }}</v-data-list-label>
                  <v-data-list-text>
                    <v-row no-gutters>
                      <div>
                        {{ $t('message.balance_info', { remainingDays: airtimeBalance.remainingDays }) }}
                        <template v-if="airtimeBalance.expiresAt">
                          {{
                            $t('message.balance_expiration', {
                              expirationDate: $moment.tz(airtimeBalance.expiresAt, 'Europe/Madrid').format('L')
                            })
                          }}
                        </template>
                      </div>
                      <v-spacer></v-spacer>
                      <v-btn
                        :disabled="!!agreement.automaticRenewal"
                        text
                        small
                        color="primary"
                        @click="
                          showPeriodCreateDialog = true
                          selectedBalanceAirtimePlan = airtimeBalance.airtimePlan
                        "
                      >
                        {{ $t('message.add_activation') }}
                      </v-btn>
                    </v-row>
                  </v-data-list-text>
                </v-data-list-item>
              </v-data-list>

              <v-card-text class="text-caption">{{ $t('message.balance_timezone_warning') }}</v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12">
            <OrdersSummary :agreement="agreement" />
          </v-col>
          <v-col cols="12">
            <PaymentMethod
              v-if="'fixed_term' === agreement.servicePlan.type"
              :agreement="agreement"
              @reload="$emit('reload')"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="4" class="py-0">
        <v-row>
          <v-col cols="12" sm="6" md="12">
            <v-card outlined class="fill-height">
              <v-toolbar flat>
                <v-toolbar-title>{{ $t('message.your_service') }}</v-toolbar-title>
              </v-toolbar>

              <v-card-text class="pt-0">
                {{ $t('message.your_service_info') }}
              </v-card-text>

              <v-data-list>
                <v-data-list-item>
                  <v-data-list-label>{{ $t('message.service') }}</v-data-list-label>
                  <v-data-list-text>{{ agreement.servicePlan.name }}</v-data-list-text>
                </v-data-list-item>
                <v-data-list-item>
                  <v-data-list-label>{{ $t('message.technology') }}</v-data-list-label>
                  <v-data-list-text>{{ $t('message.' + agreement.servicePlan.technology) }}</v-data-list-text>
                </v-data-list-item>
                <v-data-list-item>
                  <v-data-list-label>{{ $t('message.status') }}</v-data-list-label>
                  <v-data-list-text>
                    <template v-if="agreement.contractuallyActivated">
                      <v-icon color="green" size="16">mdi-circle</v-icon>
                      {{ $t('message.active') }}
                    </template>
                    <template v-else>
                      <v-icon color="red" size="16">mdi-circle</v-icon>
                      {{ 'recurring' === agreement.servicePlan.type ? $t('message.stopped') : $t('message.inactive') }}
                    </template>
                  </v-data-list-text>
                </v-data-list-item>
                <v-data-list-item v-if="agreement.iccid">
                  <v-data-list-label>
                    ICCID
                  </v-data-list-label>
                  <v-data-list-text>
                    {{ agreement.iccid }}
                  </v-data-list-text>
                </v-data-list-item>
                <v-data-list-item v-if="agreement.puk">
                  <v-data-list-label>
                    PIN
                  </v-data-list-label>
                  <v-data-list-text>
                    {{ agreement.pin }}
                  </v-data-list-text>
                </v-data-list-item>
                <v-data-list-item v-if="agreement.puk">
                  <v-data-list-label>
                    PUK
                  </v-data-list-label>
                  <v-data-list-text>
                    {{ agreement.puk }}
                  </v-data-list-text>
                </v-data-list-item>
                <template v-if="'fixed_term' === agreement.servicePlan.type && filteredPeriods">
                  <v-data-list-item
                    v-if="automaticRenewalPeriod"
                    :key="agreement.automaticRenewal"
                    link
                    link-icon="mdi-pencil"
                    @click="renewalDialog = true"
                  >
                    <v-data-list-label>
                      {{ $t('message.renewal') }}
                    </v-data-list-label>
                    <v-data-list-text :placeholder="$t('message.no_automatic_renewal_placeholder')">
                      <template v-if="agreement.automaticRenewal">
                        {{ $t('message.renewal_' + agreement.automaticRenewal) }}
                      </template>
                    </v-data-list-text>
                  </v-data-list-item>
                </template>
                <v-data-list-item v-if="agreement.endsAt">
                  <v-data-list-label>
                    {{ $t('message.expires') }}
                  </v-data-list-label>
                  <v-data-list-text>
                    {{ agreement.endsAt | moment('LL') }}
                  </v-data-list-text>
                </v-data-list-item>
                <v-data-list-item
                  v-if="
                    'recurring' === agreement.servicePlan.type &&
                      agreement.maxDeactivation &&
                      !agreement.servicePlan.maxDeactivation
                  "
                >
                  <v-data-list-label>{{ $t('message.remaining_stops') }}</v-data-list-label>
                  <v-data-list-text>
                    {{ agreement.remainingDeactivationDays[showDate.getFullYear()] }}
                    {{ $t('message.days') }}
                  </v-data-list-text>
                </v-data-list-item>
              </v-data-list>
            </v-card>
          </v-col>
          <v-col v-if="site && site['@type'] === 'Vessel'" cols="12" sm="6" md="12" class="fill-height">
            <v-card v-if="site" outlined>
              <v-toolbar flat>
                <v-toolbar-title>{{ $t('message.your_site') }}</v-toolbar-title>
              </v-toolbar>

              <v-card-text class="pt-0">
                {{ $t('message.your_site_info') }}
              </v-card-text>

              <v-data-list>
                <v-data-list-item>
                  <v-data-list-label>{{ $t('message.organization') }}</v-data-list-label>
                  <v-data-list-text>{{ site.organization.name }}</v-data-list-text>
                </v-data-list-item>
                <v-data-list-item v-if="site.callsign">
                  <v-data-list-label>Call Sign</v-data-list-label>
                  <v-data-list-text>{{ site.callsign }}</v-data-list-text>
                </v-data-list-item>
                <v-data-list-item v-if="site.imo">
                  <v-data-list-label>IMO</v-data-list-label>
                  <v-data-list-text>{{ site.imo }}</v-data-list-text>
                </v-data-list-item>
                <v-data-list-item v-if="site.mmsi">
                  <v-data-list-label>MMSI</v-data-list-label>
                  <v-data-list-text>{{ site.mmsi }}</v-data-list-text>
                </v-data-list-item>
              </v-data-list>
              <v-divider></v-divider>
              <v-card-actions>
                <v-btn
                  target="_blank"
                  :href="fleetCommanderHost + '/vessels/map/' + $iriToId(site['@id'])"
                  text
                  color="primary"
                >
                  <v-avatar size="16" tile class="mr-1">
                    <img src="https://file.usail.es/static/fleet-commander-icon-black.svg" alt="Fleet Commander" />
                  </v-avatar>
                  {{ $t('message.show_in_fleet_commander') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="site && site.networkingDevices.length > 0" cols="12" sm="6" md="12">
            <v-card outlined class="fill-height">
              <v-toolbar flat>
                <v-toolbar-title>{{ $t('message.devices') }} </v-toolbar-title>
              </v-toolbar>

              <v-card-text class="pt-0">
                {{ $t('message.devices_info') }}
              </v-card-text>

              <v-data-list>
                <template v-for="networkingDevice in sortedNetworkingDevices">
                  <v-data-list-item
                    v-if="'antenna' === networkingDevice.model.category"
                    :key="networkingDevice.id"
                    link
                    @click="showAntenna(networkingDevice)"
                  >
                    <v-data-list-label>{{
                      $t('message.' + networkingDevice.model.category.toLowerCase())
                    }}</v-data-list-label>
                    <v-data-list-text>
                      <div>
                        {{ networkingDevice.model.name }}
                      </div>
                      <div>
                        <small class="grey--text"> SN: {{ networkingDevice.serial }} </small>
                      </div>
                      <div>
                        <small v-if="networkingDevice.antenna" class="grey--text">
                          {{ $t('message.altitude') }}: {{ networkingDevice.antenna.altitude }}m
                        </small>
                      </div>
                    </v-data-list-text>
                  </v-data-list-item>
                  <v-data-list-item
                    v-else-if="networkingDevice.model.category !== 'undefined'"
                    :key="networkingDevice.id"
                  >
                    <v-data-list-label>{{
                      $t('message.' + networkingDevice.model.category.toLowerCase())
                    }}</v-data-list-label>
                    <v-data-list-text>
                      <div>
                        {{ networkingDevice.model.name }}
                      </div>
                      <div>
                        <small v-if="networkingDevice.serial" class="grey--text">
                          SN: {{ networkingDevice.serial }}
                        </small>
                      </div>
                      <div>
                        <small v-if="networkingDevice.antenna" class="grey--text">
                          {{ $t('message.altitude') }}: {{ networkingDevice.antenna.altitude }}m
                        </small>
                      </div>
                    </v-data-list-text>
                  </v-data-list-item>
                </template>
              </v-data-list>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <PeriodDialog
      :selected-period="selectedPeriod"
      :is-suspended="
        currentPeriod &&
          selectedPeriod &&
          currentPeriod.airtimePlan &&
          currentPeriod.airtimePlan.monthlyVolume &&
          selectedPeriod['@id'] === currentPeriod['@id'] &&
          !agreement.contractuallyActivated
      "
      @close="selectedPeriod = null"
      @payNow="payNow"
      @periodDeleted="periodDeleted"
    />

    <StopCreateDialog
      v-if="'recurring' === agreement.servicePlan.type"
      :show="showPeriodCreateDialog"
      :reset="showPeriodCreateDialog === false"
      :agreement-id="agreement['@id']"
      :starts-at="selectedDay"
      :ends-at="selectedDay"
      :periods="agreement.periods"
      @periodCreated="periodCreated"
      @paymentCreated="paymentCreated"
      @cardError="paymentCardError = true"
      @close="showPeriodCreateDialog = false"
    ></StopCreateDialog>

    <ActivationCreateDialog
      v-if="'on_demand' === agreement.servicePlan.type || 'fixed_term' === agreement.servicePlan.type"
      :show="showPeriodCreateDialog"
      :reset="showPeriodCreateDialog === false"
      :agreement-id="agreement['@id']"
      :starts-at="selectedDay"
      :ends-at="selectedDay"
      :initial-airtime-plan="selectedBalanceAirtimePlan"
      @periodCreated="periodCreated"
      @paymentCreated="paymentCreated"
      @cardError="paymentCardError = true"
      @close="showPeriodCreateDialog = false"
    ></ActivationCreateDialog>

    <v-dialog v-if="reactivableStop" v-model="showStopReactivateDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">{{ $t('message.reactivation_request') }}</v-card-title>

        <v-card-text>
          <p v-if="agreement.networkPlatform.scheduleAnticipation > 0">
            {{ $tc('message.reactivate_info.paragraph_1', agreement.networkPlatform.scheduleAnticipation) }}
          </p>
          <p>
            {{ $t('message.reactivate_info.paragraph_2', { date: $moment(minStartDate).format('LL') }) }}
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="green darken-1" text @click="showStopReactivateDialog = false">{{
            $t('message.cancel')
          }}</v-btn>

          <v-btn color="green darken-1" text @click="reactivateStop(reactivableStop)">{{
            $t('message.reactivate')
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="payment"
      v-model="showPaymentInstructionsDialog"
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition"
      max-width="900px"
      persistent
    >
      <v-card>
        <template v-if="$vuetify.breakpoint.smAndUp">
          <v-toolbar elevation="0" dense color="primary">
            <v-spacer></v-spacer>

            <v-btn icon @click="showPaymentInstructionsDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </template>

        <v-toolbar v-else elevation="0" color="primary">
          <v-btn icon dark @click="showPaymentInstructionsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <PaymentInstructionsDialogContent :payment="payment" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showPaymentInstructionsDialog = false">{{ $t('message.finish') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="payPendingDialogShow"
      max-width="400"
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition"
    >
      <PayPendingDialog
        v-if="selectedPeriod && !selectedPeriod.paid && getPendingOrder(selectedPeriod)"
        :order="getPendingOrder(selectedPeriod)"
        @close="payPendingDialogShow = false"
        @paymentCreated="pendingPaymentPaid"
      ></PayPendingDialog>
    </v-dialog>

    <v-dialog v-model="showAntennaDialog" max-width="400">
      <v-card>
        <template v-if="selectedAntenna.antenna">
          <v-card-title class="headline">
            {{ $t('message.antenna_details') }}
          </v-card-title>

          <v-card-text>
            <div class="subtitle-2">{{ $t('message.antenna') }}</div>
            <div class="mb-3">{{ selectedAntenna.antenna.name }}</div>
            <div class="subtitle-2">{{ $t('message.altitude') }}</div>
            <div>{{ selectedAntenna.antenna.altitude }} m</div>

            <template v-if="selectedAntenna.antenna.obstacles.length > 0">
              <div class="title my-3">{{ $t('message.obstacles') }}</div>

              <v-card v-for="obstacle in selectedAntenna.antenna.obstacles" :key="obstacle['@id']" outlined>
                <v-card-text>
                  <div class="subtitle-2">{{ obstacle.name }}</div>
                </v-card-text>
                <v-data-list>
                  <v-data-list-item>
                    <v-data-list-label>{{ $t('message.width') }}</v-data-list-label>
                    <v-data-list-text>{{ obstacle.width }} m</v-data-list-text>
                  </v-data-list-item>
                  <v-data-list-item>
                    <v-data-list-label>{{ $t('message.altitude') }}</v-data-list-label>
                    <v-data-list-text>{{ obstacle.altitude }} m</v-data-list-text>
                  </v-data-list-item>
                  <v-data-list-item>
                    <v-data-list-label>{{ $t('message.distance') }}</v-data-list-label>
                    <v-data-list-text>{{ obstacle.distance }} m</v-data-list-text>
                  </v-data-list-item>
                  <v-data-list-item>
                    <v-data-list-label>{{ $t('message.relative_bearing') }}</v-data-list-label>
                    <v-data-list-text>{{ obstacle.altitude }} º</v-data-list-text>
                  </v-data-list-item>
                </v-data-list>
              </v-card>
            </template>
          </v-card-text>
        </template>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="showAntennaDialog = false">{{ $t('message.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="'fixed_term' === agreement.servicePlan.type" v-model="renewalDialog" max-width="400">
      <v-form @submit.prevent="updateRenewalTerm">
        <v-card v-if="filteredPeriods">
          <v-toolbar flat dark color="primary" class="px-2">
            <v-toolbar-title>{{ $t('message.set_automatic_renewal') }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon dark @click="renewalDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-row v-if="renewalUpdateLoading" no-gutters justify="center" class="py-12">
            <v-progress-circular indeterminate color="primary" />
          </v-row>
          <v-card-text v-else>
            <div class="mb-6">
              <v-radio-group v-model="newRenewalTerm" hide-details>
                <v-radio
                  v-for="allowedFixedTerm in agreement.servicePlan.allowedFixedTerms"
                  :key="allowedFixedTerm"
                  :label="$t('message.renewal_' + allowedFixedTerm)"
                  :value="allowedFixedTerm"
                ></v-radio>
                <v-radio :label="$t('message.no_automatic_renewal')" value="no_renewal"></v-radio>
              </v-radio-group>
            </div>

            <v-card v-if="automaticRenewalPeriod" flat color="grey lighten-4">
              <v-card-text>
                <div>
                  <div>{{ $t('message.next_automatic_renewal_period') }}:</div>
                  <div class="body-1">
                    {{ $moment(automaticRenewalPeriod.endsAt).format('LL') }}
                  </div>
                </div>

                <div v-if="'no_renewal' !== newRenewalTerm" class="mt-6">
                  <div>{{ $t('message.gross_total_monthly') }}:</div>

                  <template v-if="renewalDefaultPrice && !renewalDefaultPriceLoading">
                    <template v-if="renewalDefaultPrice.feesCount > 0">
                      <div class="headline">
                        {{ $n(renewalDefaultPrice.monthlyFee.netPrice, agreement.currency.code) }}
                      </div>
                      <div class="caption grey--text">
                        {{ $n(renewalDefaultPrice.monthlyFee.grossPrice, agreement.currency.code) }}
                        ({{
                          $t('message.tax_incl', {
                            tax: renewalDefaultPrice.monthlyFee.tax
                          })
                        }})
                      </div>
                    </template>

                    <template v-else>
                      <div class="headline">
                        {{ $n(renewalDefaultPrice.initialCharge.netPrice, agreement.currency.code) }}
                      </div>
                      <div class="caption grey--text">
                        {{ $n(renewalDefaultPrice.initialCharge.grossPrice, agreement.currency.code) }}
                        ({{
                          $t('message.tax_incl', {
                            tax: renewalDefaultPrice.initialCharge.tax
                          })
                        }})
                      </div>
                    </template>
                  </template>

                  <v-progress-circular
                    v-if="renewalDefaultPriceLoading"
                    size="25"
                    indeterminate
                    color="primary"
                    class="mt-3"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn :disabled="renewalUpdateLoading" color="default" text @click="renewalDialog = false">Cancel</v-btn>
            <v-btn :disabled="renewalUpdateLoading" color="primary" text type="submit">Accept</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <v-dialog v-model="stopRenewalDialog" max-width="400">
      <v-card>
        <v-toolbar flat dark color="red" class="px-2">
          <v-toolbar-title>{{ $t('message.set_automatic_renewal') }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="stopRenewalDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <div class="mb-4 mt-6">
            <p>{{ $t('message.alert_automatic_renewal') }}</p>
            <v-row v-if="renewalUpdateLoading" no-gutters justify="center" class="py-12">
              <v-progress-circular indeterminate color="primary" />
            </v-row>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="renewalUpdateLoading" color="default" text @click="stopRenewalDialog = false">Cancel</v-btn>
          <v-btn :disabled="renewalUpdateLoading" color="primary" text @click="stopAutomaticRenewal()">Accept</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <VolumeTokenCreateDialog
      v-if="currentPeriod && hasAllowedTokens"
      :show="showVolumeTokenCreateDialog"
      :service-period="currentPeriod"
      @volumeTokenCreated="volumeTokenCreated"
      @paymentCreated="paymentCreated"
      @cardError="paymentCardError = true"
      @close="showVolumeTokenCreateDialog = false"
    ></VolumeTokenCreateDialog>
  </div>
</template>
<script>
import fetch from '@/utils/fetch'
import { CalendarView } from 'vue-simple-calendar'
import AppCalendarViewHeader from '@/components/AgreementManage/AppCalendarViewHeader'
import PeriodDialog from '@/components/AgreementManage/PeriodDialog'
import ActivationCreateDialog from '@/components/ActivationCreateDialog/Dialog'
import StopCreateDialog from '@/components/StopCreateDialog'
import PaymentInstructionsDialogContent from '@/components/AgreementManage/PaymentInstructionsDialogContent'
import PayPendingDialog from '@/components/AgreementManage/PayPendingDialog'
import OrdersSummary from '@/components/AgreementManage/OrdersSummary'
import VolumeTokenCreateDialog from '@/components/VolumeTokenCreateDialog'
import Alert from '@/components/AgreementManage/Alert'
import CurrentMonthTokens from '@/components/AgreementManage/CurrentMonthTokens'
import PaymentMethod from '@/components/AgreementManage/PaymentMethod'
import YearCalendar from '@/components/AgreementManage/YearCalendar'

export default {
  name: 'AgreementManage',
  components: {
    YearCalendar,
    PaymentMethod,
    CurrentMonthTokens,
    Alert,
    VolumeTokenCreateDialog,
    OrdersSummary,
    PeriodDialog,
    AppCalendarViewHeader,
    PaymentInstructionsDialogContent,
    PayPendingDialog,
    StopCreateDialog,
    ActivationCreateDialog,
    CalendarView
  },
  props: {
    agreement: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      activeTab: 0,
      servicePeriods: null,
      showDate: new Date(),
      showPeriodCreateDialog: false,
      showStopReactivateDialog: false,
      showPaymentInstructionsDialog: false,
      selectedPeriod: null,
      selectedPendingPeriod: null,
      selectedDay: null,
      fleetCommanderHost: process.env.VUE_APP_FLEET_COMMANDER_HOST,
      site: null,
      isLoadingSite: false,
      isLoadingPeriods: false,
      isLoadingPlan: false,
      payment: null,
      payPendingDialogShow: false,
      paymentCardError: false,
      showAntennaDialog: false,
      selectedAntenna: {},
      showVolumeTokenCreateDialog: false,
      renewalDialog: false,
      stopRenewalDialog: false,
      automaticRenewal: null,
      newRenewalTerm: null,
      renewalUpdateLoading: false,
      renewalDefaultPrice: null,
      renewalDefaultPriceLoading: false,
      selectedBalanceAirtimePlan: null,
      currentPeriod: null,
      isLoadingPayPeriod: null
    }
  },

  computed: {
    positiveAirtimeBalance() {
      let result = []
      result = this.agreement.airtimeBalances.filter(airtimeBalance => {
        return airtimeBalance.remainingDays > 0
      })
      return result
    },
    minStartDate() {
      if (!this.agreement) {
        return
      }
      let date = this.$moment()
        .tz('Europe/Madrid')
        .add(this.agreement.networkPlatform.scheduleAnticipation, 'days')
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })

      if (
        this.$moment()
          .tz('Europe/Madrid')
          .hour() >= 8 &&
        'recurring' === this.agreement.servicePlan.type
      ) {
        date = date.add(1, 'days')
      }

      return date.toDate()
    },

    sortedNetworkingDevices: function() {
      let sorted = this.site.networkingDevices

      sorted.sort((a, b) => {
        let categoryA = a.model.category
        let categoryB = b.model.category

        if (categoryA === 'antenna') {
          return -1
        }
        if (categoryA === 'modem' && categoryB !== 'antenna') {
          return -1
        }
        if (this.$t('message.' + categoryA) < this.$t('message.' + categoryB)) {
          return -1
        }
        if (this.$t('message.' + categoryA) > this.$t('message.' + categoryB)) {
          return 1
        }
        return 0
      })

      return sorted
    },

    currentAgreements: function() {
      let currentAgreements = []

      if (this.site.connectAgreements.length > 0) {
        this.site.connectAgreements.forEach(agreement => {
          if (
            this.$moment() > this.$moment(agreement.startsAt) &&
            (!agreement.endsAt || this.$moment() < this.$moment(agreement.endsAt))
          ) {
            currentAgreements.push(agreement)
          }
        })
      }

      return currentAgreements
    },

    periodCreateDialogSize: function() {
      return this.agreement.servicePlan.type === 'recurring' ? 400 : 600
    },

    eventTop: function() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return '2em'
        default:
          return '2.4em'
      }
    },

    automaticRenewalPeriod() {
      if (!this.filteredPeriods) {
        return null
      }

      if (this.filteredPeriods.current.length > 0) {
        return this.filteredPeriods.current[0]
      } else if (this.filteredPeriods.incoming.length > 0) {
        return this.filteredPeriods.incoming[0]
      }

      return null
    },

    filteredPeriods: function() {
      if (this.servicePeriods) {
        const filteredPeriods = {
          current: [],
          incoming: []
        }
        this.servicePeriods.forEach(period => {
          if (
            (new Date(period.startsAt) <= new Date() && !period.endsAt) ||
            (new Date(period.startsAt) <= new Date() && new Date(period.endsAt) >= new Date())
          ) {
            filteredPeriods.current.push(period)
          } else if (new Date(period.startsAt) > new Date()) {
            filteredPeriods.incoming.push(period)
          }
        })

        return filteredPeriods
      }

      return null
    },

    reactivableStop: function() {
      if (this.filteredPeriods && this.filteredPeriods.current.length > 0) {
        if (
          'stop' === this.filteredPeriods.current[0].type &&
          Math.abs(new Date() - new Date(this.filteredPeriods.current[0].endsAt)) / 36e5 > 24
        ) {
          return this.filteredPeriods.current[0]
        }
      }
      return null
    },

    dateClasses: function() {
      if (!this.minStartDate) {
        return null
      }

      if (this.servicePeriods) {
        let dateClasses = {}
        this.servicePeriods.forEach(period => {
          const endsAt =
            new Date(period.endsAt).getHours > 8
              ? this.$moment(period.endsAt)
              : this.$moment(period.endsAt).subtract(1, 'days')
          let currentDate = this.$moment(period.startsAt).set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0
          })
          let endDate = period.endsAt
            ? endsAt.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            : this.$moment().set({
                hour: 0,
                minute: 0,
                second: 0,
                millisecond: 0
              })

          while (currentDate <= endDate) {
            dateClasses[currentDate.format('YYYY-MM-DD')] = 'has-event'
            currentDate = currentDate.add(1, 'days')
          }
        })

        let now = this.$moment()

        while (now <= this.minStartDate) {
          dateClasses[now.format('YYYY-MM-DD')] += ' past'
          now.add(1, 'days')
        }

        return dateClasses
      }

      return null
    },

    nextPeriod() {
      if (!this.filteredPeriods || 0 === this.filteredPeriods.length || 0 === this.filteredPeriods.incoming.length) {
        return
      }

      return this.filteredPeriods.incoming[0]
    },

    hasAllowedTokens() {
      return (
        this.currentPeriod && this.currentPeriod.airtimePlan && this.currentPeriod.airtimePlan.allowedTokens.length > 0
      )
    }
  },

  watch: {
    filteredPeriods(periods) {
      if (!periods || 0 === periods.length || 0 === periods.current.length) {
        return
      }

      return fetch(periods.current[0]['@id'])
        .then(response => response.json())
        .then(data => {
          this.currentPeriod = data
        })
    },
    agreement: {
      handler(agreement) {
        this.fetchAll(agreement.site['@id'], agreement['@id'])
      },
      deep: true
    },
    showPeriodCreateDialog: function(showPeriodCreateDialog) {
      if (showPeriodCreateDialog === false) {
        this.selectedBalanceAirtimePlan = null
        if (this.paymentCardError) {
          this.fetchServicePeriods(this.agreement['@id'])
          this.paymentCardError = false
        }
      }
    },

    newRenewalTerm: function(newRenewalTerm) {
      this.fetchRenewalDefaultPrice(newRenewalTerm)
    },

    automaticRenewalPeriod: function(automaticRenewalPeriod) {
      if (!automaticRenewalPeriod) {
        return
      }

      this.newRenewalTerm = this.agreement.automaticRenewal
    }
  },

  mounted() {
    this.fetchAll(this.agreement.site['@id'], this.agreement['@id'])
    if ('fixed_term' === this.agreement.servicePlan.type && !this.agreement.automaticRenewal) {
      this.newRenewalTerm = 'no_renewal'
    }
  },

  methods: {
    stopAutomaticRenewal() {
      this.renewalUpdateLoading = true

      fetch(this.agreement['@id'], {
        method: 'PUT',
        headers: new Headers({ 'Content-Type': 'application/ld+json' }),
        body: JSON.stringify({ automaticRenewal: this.automaticRenewal })
      })
        .then(response => response.json())
        .then(() => {
          this.$emit('reload')

          this.stopRenewalDialog = false
          this.renewalUpdateLoading = false
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
          this.stopRenewalDialog = false

          this.renewalUpdateLoading = false
        })
    },
    payPeriod(period) {
      this.isLoadingPayPeriod = period

      fetch(period['@id'])
        .then(response => response.json())
        .then(data => {
          this.isLoadingPayPeriod = null
          this.$router.push({
            name: 'order',
            params: { id: this.$iriToId(this.getPendingOrder(data)['@id']) }
          })
        })
        .catch(() => {
          this.isLoadingPayPeriod = null
        })
    },

    fetchRenewalDefaultPrice: function(renewalTerm) {
      if (!renewalTerm || 'no_renewal' === renewalTerm) {
        this.renewalDefaultPrice = null
        return
      }

      let startsAt = this.$moment(this.automaticRenewalPeriod.endsAt).toISOString()

      let endsAt

      if ('semester' === renewalTerm) {
        endsAt = this.$moment(startsAt)
          .add(6, 'month')
          .toISOString()
      } else {
        endsAt = this.$moment(startsAt)
          .add(1, renewalTerm)
          .toISOString()
      }

      const agreementId = this.agreement.id

      this.renewalDefaultPriceLoading = true

      const headers = new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json'
      })
      let url =
        this.automaticRenewalPeriod.airtimePlan['@id'] +
        '/default_price?startsAt=' +
        startsAt +
        '&endsAt=' +
        endsAt +
        '&agreement=' +
        agreementId
      fetch(url, { headers: headers })
        .then(response => {
          return response.json()
        })
        .then(data => {
          this.renewalDefaultPrice = data
          this.renewalDefaultPriceLoading = false
        })
        .catch(e => {
          this.renewalDefaultPriceLoading = false
          // eslint-disable-next-line no-console
          console.log(e)
          //this.pricesKey = new Date().getTime()
        })
    },

    updateRenewalTerm() {
      this.renewalUpdateLoading = true

      if ('no_renewal' === this.newRenewalTerm) {
        this.newRenewalTerm = null
      }
      fetch(this.agreement['@id'], {
        method: 'PUT',
        headers: new Headers({ 'Content-Type': 'application/ld+json' }),
        body: JSON.stringify({ automaticRenewal: this.newRenewalTerm })
      })
        .then(response => response.json())
        .then(() => {
          this.renewalDialog = false
          this.renewalUpdateLoading = false
          this.$emit('reload')
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
          this.renewalDialog = false
          this.renewalUpdateLoading = false
        })
    },

    showAntenna(antenna) {
      this.selectedAntenna = antenna
      this.showAntennaDialog = true
    },

    getTechnologyIcon(technology) {
      switch (technology) {
        case 'vsat':
        case 'l_band':
          return 'mdi-satellite-variant'
        case '3g_lte':
          return 'mdi-antenna'
        default:
          return 'mdi-circle'
      }
    },

    payNow() {
      this.payPendingDialogShow = true
    },

    periodsToEvents(periods) {
      let events = []
      periods.forEach(period => {
        if ('stop' === period.type && !period.endsAt) {
          let endate = new Date(period.startsAt).setHours(23, 59, 59)

          if (new Date(period.startsAt) < new Date()) {
            endate = new Date().setHours(23, 59, 59)
          }

          events.push({
            id: period['@id'],
            startDate: new Date(period.startsAt),
            endDate: endate,
            title: this.$t('message.indefinite_stop'),
            classes: 'red white--text no-ending-stop',
            period: period
          })
        } else {
          let classes = 'white--text'
          let title = ''

          if ('stop' === period.type) {
            classes = classes + ' red'
            title = this.$t('message.stopped')
          } else if ('swap' === period.type) {
            classes = classes + ' blue'
            title = this.$t('message.swap')
          } else {
            if (
              this.currentPeriod &&
              this.currentPeriod.airtimePlan &&
              this.currentPeriod.airtimePlan.monthlyVolume &&
              period['@id'] === this.currentPeriod['@id'] &&
              !this.agreement.contractuallyActivated
            ) {
              if ('suspended_for_non_payment' === this.agreement.expectedStatus) {
                classes = classes + ' red'
                title = this.$t('message.suspended')
              } else {
                classes = classes + ' orange'
                title = this.$t('message.activating')
              }
            } else {
              classes = classes + ' green'
              title = this.$t('message.active')
            }

            if (!period.paid) {
              classes = classes + ' payment-pending'
              title = this.$t('message.payment_pending')
            }
          }

          events.push({
            id: period['@id'],
            startDate: new Date(period.startsAt),
            endDate:
              new Date(period.endsAt).getHours > 8
                ? new Date(period.endsAt)
                : this.$moment(period.endsAt)
                    .subtract(1, 'days')
                    .toDate(),
            title: title,
            classes: classes,
            period: period
          })
        }
      })
      return events
    },

    fetchAll(vesselId, agreementId) {
      this.site = null
      this.servicePeriods = null
      this.fetchVessel(vesselId)
      this.fetchServicePeriods(agreementId)
    },

    fetchVessel: function(vesselId) {
      this.site = null
      this.isLoadingSite = true
      fetch(vesselId)
        .then(response => {
          return response.json()
        })
        .then(data => {
          this.site = data
          this.isLoadingSite = false
        })
        .catch(() => {
          this.isLoadingSite = false
        })
    },

    fetchServicePeriods: function(agreementId) {
      this.agreementPeriods = null
      this.isLoadingPeriods = true
      this.currentPeriod = null
      fetch('/connect_service_periods?agreement=' + agreementId + '&order[startsAt]=asc&pagination=false')
        .then(response => {
          return response.json()
        })
        .then(data => {
          this.servicePeriods = data['hydra:member']
          this.isLoadingPeriods = false
        })
        .catch(() => {
          this.isLoadingPeriods = false
        })
    },

    setShowDate(d) {
      this.showDate = d
    },

    clickCalendarEvent(event) {
      if ('period' in event) {
        this.openPeriodDialog(event.period)
      } else {
        this.openPeriodDialog(event.originalEvent.period)
      }
    },

    openPeriodDialog(period) {
      this.selectedPeriod = period
    },

    getPendingOrder(period) {
      if (period.paid) {
        return
      }

      if ('on_demand' === period.agreement.servicePlan.type) {
        return period.orderLines[0].parentOrder
      }

      if ('fixed_term' === period.agreement.servicePlan.type) {
        let pendingOrder = null
        period.orderLines.forEach(orderLine => {
          if (this.$moment(orderLine.parentOrder.issuedAt).isSame(this.$moment(), 'month')) {
            pendingOrder = orderLine.parentOrder
          }
        })

        return pendingOrder
      }
    },

    clickCalendarDay(day) {
      let hasEvent = false

      for (let date in this.dateClasses) {
        if (this.$moment(day).format('YYYY-MM-DD') === date) {
          hasEvent = true
        }
      }

      let minDate = new Date(
        this.minStartDate.getFullYear(),
        this.minStartDate.getMonth(),
        this.minStartDate.getDate(),
        0,
        0,
        0
      )

      if (!hasEvent && day >= minDate) {
        this.showPeriodCreateDialog = true
        this.selectedDay = day
      }
    },

    periodCreated(period) {
      if ('stop' === period.type) {
        this.showPeriodCreateDialog = false
      }

      if ('fixed_term' === period.agreement.servicePlan.type) {
        this.payment = period.orderLines[0].parentOrder.payments[0]
        this.showPeriodCreateDialog = false
        this.showPaymentInstructionsDialog = true
      }

      this.$store.commit('pendingOrdersSetOrders', null)
      this.$emit('reload')
    },

    volumeTokenCreated() {
      this.$store.commit('pendingOrdersSetOrders', null)
      this.$emit('reload')
    },

    paymentCreated(payment) {
      this.payment = payment
      this.showPeriodCreateDialog = false
      this.showPaymentInstructionsDialog = true

      this.$store.commit('pendingOrdersSetOrders', null)
      this.$emit('reload')
    },

    pendingPaymentPaid() {
      this.payPendingDialogShow = false
      this.selectedPeriod = null

      this.$store.commit('pendingOrdersSetOrders', null)
      this.$emit('reload')
    },

    reactivateStop(period) {
      let editedPeriod = { '@id': period['@id'] }
      editedPeriod.endsAt = this.minStartDate

      fetch(editedPeriod['@id'], {
        method: 'PUT',
        headers: new Headers({ 'Content-Type': 'application/ld+json' }),
        body: JSON.stringify(editedPeriod)
      })
        .then(response => {
          return response.json()
        })
        .then(() => {
          this.fetchServicePeriods(this.agreement['@id'])
          this.showStopReactivateDialog = false
        })
        .catch(() => {
          this.showStopReactivateDialog = false
        })
    },

    periodDeleted() {
      this.selectedPeriod = null
      this.$emit('reload')
    }
  }
}
</script>

<style lang="stylus" scoped>
.list-tile-pending
    .v-list__tile__sub-title *:not(a),
    .v-list__tile__title,
    .v-icon
        opacity 0.5
</style>
